/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

export type Environment = 'local' | 'testing' | 'staging' | 'production';

const VALID_ENVIRONMENTS: readonly string[] = ['local', 'testing', 'staging', 'production'];

/**
 * Resolves an env var, preferring the runtime `window.__ENV__` injection over
 * the Vite build-time `import.meta.env`.
 *
 * @param name Env var name to look up.
 * @returns The value, or `undefined` when unset in both sources.
 */
function resolveEnvVar(name: string): string | undefined {
  const windowEnv = typeof window !== 'undefined' && (window as any).__ENV__;
  return (windowEnv && windowEnv[name]) || import.meta.env[name] || undefined;
}

/**
 * Resolves and validates the active deployment environment from `ENVIRONMENT`.
 * Defaults to `local` when unset.
 *
 * @returns The validated {@link Environment}.
 * @throws Error when the configured value is not a known environment.
 */
function resolveEnvironment(): Environment {
  const raw = resolveEnvVar('ENVIRONMENT');
  if (!raw) return 'local';
  if (!VALID_ENVIRONMENTS.includes(raw)) {
    throw new Error(
      `[FATAL] Invalid ENVIRONMENT value: "${raw}". ` +
      `Must be one of: ${VALID_ENVIRONMENTS.join(', ')}. ` +
      `Check your .env file, config.js, or pipeline variable group configuration.`,
    );
  }
  return raw as Environment;
}

export const ENVIRONMENT: Environment = resolveEnvironment();

export const isProduction = ENVIRONMENT === 'production';
export const isNonProduction = !isProduction;

/** Env values considered truthy for boolean flags (case-insensitive). */
const TRUTHY_FLAG_VALUES: readonly string[] = ['1', 'true', 'yes', 'on'];

/**
 * Resolves a boolean feature flag from an env var.
 *
 * Only `1`/`true`/`yes`/`on` (case-insensitive) are truthy; anything else,
 * including unset/empty, is false. Mirrors the ecosystem flag convention.
 *
 * @param name Env var name to look up.
 * @returns `true` when the value is in the truthy set, `false` otherwise.
 */
function resolveFlag(name: string): boolean {
  const raw = resolveEnvVar(name);
  if (!raw) return false;
  return TRUTHY_FLAG_VALUES.includes(raw.trim().toLowerCase());
}

/**
 * Whether screenshot mode is enabled via the `SCREENSHOT_MODE` env var.
 *
 * When active, the dev/local environment chrome (e.g. the environment bookmark
 * ribbon) is hidden so clean screenshots of the local environment can be taken.
 * This is the headless/automated activation path; the Dev Cockpit also exposes
 * a runtime toggle for the same effect.
 */
export const isScreenshotMode: boolean = resolveFlag('SCREENSHOT_MODE');
